import BaseSelect from "../../../components/ui/BaseSelect";
import { Avatar, AvatarGroup, Badge } from "@mui/material";
import { TeamTaskStatusType } from "../../../utils/enum";
import dayjs from "dayjs";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { updateTaskStatus } from "../../../services/api/teamTask";
import { Oval } from "react-loader-spinner";
import { brandSecondaryColor } from "../../../utils/variables";
import UserProfileLink from "../../../components/ui/UserProfileLink";

const PRIORITY_ICON = {
  LOW: <i className="fa-light fa-angle-up text-[#EDC131]"></i>,
  MEDIUM: <i className="fa-light fa-angles-up text-[#219B99]"></i>,
  HIGH: (
    <div className="flex flex-col justify-center text-center ml-1">
      <i className="fa-light fa-angle-up h-[9px] text-[#FF0004]"></i>
      <i className="fa-light fa-angles-up text-[#FF0004]"></i>
    </div>
  ),
};

const PRIORITY_ICON_COLOR = {
  LOW: "#EDC131",
  MEDIUM: "#219B99",
  HIGH: "#FF0004",
};

const PRIORITY = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
};

const TaskCard = ({
  title,
  taskId,
  referenceId,
  description,
  status,
  dueDate,
  priority,
  assignees,
  workOrder,
  totalComments,
  onClick,
  refetchAllTeamTask,
  isDragging,
}) => {
  const navigate = useNavigate();
  const {
    isPending: isUpdateTaskStatusMutationApiLoading,
    error: isUpdateTaskStatusMutationApiError,
    data: updatedUser,
    mutateAsync: updateTaskStatusMutation,
  } = useMutation({
    mutationFn: updateTaskStatus,
    enabled: false,
  });

  const handleTaskStatusUpdate = async (status) => {
    try {
      const payload = {
        taskId,
        status,
      };
      await updateTaskStatusMutation(payload);
      await refetchAllTeamTask();
    } catch (error) {}
  };
  return (
    <div
      className={`bg-white shadow-md rounded-xl py-4 px-4 cursor-grab mb-3 ${isDragging ? "shadow-2xl ring-2 ring-secondary/30" : ""}`}
    >
      {isUpdateTaskStatusMutationApiLoading && (
        <Oval
          visible={isUpdateTaskStatusMutationApiLoading}
          height="80"
          width="80"
          colors={[brandSecondaryColor, "#ff9b60"]}
          ariaLabel="oval-loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      )}
      <div className="flex justify-between items-start gap-2">
        <p
          className="text-primary text-lg cursor-pointer capitalize break-words max-w-[70%]"
          onClick={onClick}
        >
          <span>#{referenceId} - </span> {title}
        </p>

        <div className="max-h-[29px] shrink-0">
          <BaseSelect
            className="w-32 !border-none text-center !text-white"
            options={[
              { label: "To do", value: TeamTaskStatusType.TODO, color: "#FF5D20" },
              { label: "In-Progress", value: TeamTaskStatusType.INPROCESS, color: "#3C9FD4" },
              { label: "Completed", value: TeamTaskStatusType.COMPLETED, color: "#1FA52F" },
              { label: "On Hold", value: TeamTaskStatusType.ON_HOLD, color: "#9B59B6" },
            ]}
            onChange={(e) => handleTaskStatusUpdate(e.target.value)}
            value={status}
            arrowColor="white"
          />
        </div>
      </div>
      <p
        className="text-xs mt-4 line-clamp-3"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
        dangerouslySetInnerHTML={{
          __html: (description || "").replace(/<\/?p>/g, ""),
        }}
      />
      <div className="flex mt-3 gap-3">
        {workOrder && (
          <p
            className="bg-light rounded-[4px] px-2 py-[2px] cursor-pointer text-primary"
            onClick={() => navigate(`/workOrder/details/${workOrder?.id}`)}
          >
            <i className="fa-solid fa-briefcase mr-1 text-primary"></i>#{workOrder?.referenceId}
          </p>
        )}
        {dueDate && (
          <p className="bg-light rounded-[4px] px-2 py-[2px] cursor-pointer">
            <i className="fa-light fa-hourglass-end mr-1"></i>
            {dayjs(dueDate).format("DD/MM/YYYY")}
          </p>
        )}
      </div>
      <div className="flex justify-between items-end mt-4 border-t pt-3">
        <div className="flex mb-2">
          <AvatarGroup spacing="small">
            {/* The Avatar itself is the link: AvatarGroup styles its direct
                `.MuiAvatar-root` children, so nothing may sit in between. */}
            {assignees?.map((assignee) => (
              <BaseTooltip
                key={assignee?.id}
                content={`${assignee?.firstName} ${assignee?.lastName}`}
              >
                <UserProfileLink
                  as={Avatar}
                  userId={assignee?.id}
                  linkClassName="cursor-pointer"
                  alt={`${assignee?.firstName} ${assignee?.lastName}`}
                  src={assignee?.profilePicture}
                  sx={{ width: 36, height: 36 }}
                />
              </BaseTooltip>
            ))}
          </AvatarGroup>
        </div>

        <div className="flex items-center">
          <div className="border-r pr-4 text-center" onClick={onClick}>
            <Badge
              className="cursor-pointer"
              badgeContent={totalComments}
              color="primary"
              overlap="circular"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  height: "16px",
                  minWidth: "16px",
                  padding: "0 4px",
                },
              }}
            >
              <i className="fa-light fa-comment text-primary text-xl"></i>
            </Badge>
            <p className="text-primary">Comment</p>
          </div>

          <div className={`text-center pl-4 text-[${PRIORITY_ICON_COLOR[priority]}]`}>
            {PRIORITY_ICON[priority]}
            <p className={`text-center text-[${PRIORITY_ICON_COLOR[priority]}]`}>
              {PRIORITY[priority]}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
